// Produktové sady — výhodné/dárkové sady s variantami. Shoptet neumí sady u
// produktů s variantami, proto "sada" je skutečný samostatný produkt v
// Shoptetu (zákazník u každé položky vybírá velikost zvlášť) a tady jen
// držíme, z jakých komponentních produktů se skládá a jakou má slevu.

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse, ProductSet } from '@sklad-sync/shared';
import { useCallback, useEffect, useState } from 'react';
import { ProductSetModal } from './ProductSetModal';

type Props = {
  user: MeResponse;
};

const ESHOPS: { code: string; label: string }[] = [
  { code: 'v-mart-cz', label: 'CZ' },
  { code: 'v-mart-sk', label: 'SK' },
];

export function ProductSetsListPage({ user }: Props) {
  const isAdmin = hasModule(user.modules, 'sady');
  const [eshopId, setEshopId] = useState<string>(ESHOPS[0]?.code ?? 'v-mart-cz');
  const [sets, setSets] = useState<ProductSet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [editing, setEditing] = useState<ProductSet | 'new' | null>(null);
  const [cleaning, setCleaning] = useState<string | null>(null);

  /**
   * Úklid kódů SADA-… v Shoptetu: nepoužité starší než 24 h + nahrazené.
   * Backend maže po dávkách — voláme opakovaně, dokud něco zbývá (a dokud
   * dávka něco odmaže, ať se to nezacyklí na kódu, který smazat nejde).
   */
  async function cleanupCoupons() {
    if (
      !confirm(
        'Smazat v Shoptetu nepoužité slevové kódy SADA-… starší než 3 hodiny? Použité kódy (z objednávek) zůstanou.',
      )
    ) {
      return;
    }
    let deleted = 0;
    let templates = 0;
    let failed = 0;
    setCleaning('Uklízím…');
    try {
      for (let round = 0; round < 50; round++) {
        const r = await apiFetch<{
          deleted: number;
          used: number;
          failed: number;
          remaining: number;
          templates_deleted: number;
        }>('/api/sady/kupony/cleanup', { method: 'POST' });
        deleted += r.deleted;
        templates += r.templates_deleted;
        failed = r.failed;
        setCleaning(`Uklízím… smazáno ${deleted}, zbývá ${r.remaining}`);
        if (r.remaining === 0 || r.deleted + r.used === 0) break;
      }
      setToast({
        msg: `Smazáno ${deleted} kódů${templates ? ` a ${templates} stará šablona` : ''}${failed ? `, ${failed} se nepodařilo` : ''}`,
        kind: failed ? 'error' : 'success',
      });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Úklid selhal', kind: 'error' });
    } finally {
      setCleaning(null);
    }
  }

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ sets: ProductSet[] }>(
        `/api/sady?eshop_id=${encodeURIComponent(eshopId)}`,
      );
      setSets(data.sets);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Načtení se nepodařilo');
    }
  }, [eshopId]);

  useEffect(() => {
    setSets(null);
    load();
  }, [load]);

  async function remove(s: ProductSet) {
    if (!confirm(`Smazat sadu „${s.nazev}"?`)) return;
    try {
      await apiFetch(`/api/sady/${s.id}`, { method: 'DELETE' });
      setToast({ msg: 'Sada smazána', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Produktové sady</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Výhodné/dárkové sady s variantami (velikosti) — zákazník si u každé položky vybírá
            velikost zvlášť, do košíku jdou jednotlivá SKU.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cleanupCoupons}
              disabled={cleaning !== null}
              title="Smaže v Shoptetu nepoužité slevové kódy SADA-… starší než 3 h"
              className="bg-bg border border-border hover:border-primary text-text-secondary text-sm font-medium rounded-lg px-4 py-2.5 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-wait"
            >
              {cleaning ?? 'Uklidit nepoužité kódy'}
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
            >
              Přidat sadu
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-border bg-bg p-1">
        {ESHOPS.map((e) => (
          <button
            key={e.code}
            type="button"
            onClick={() => setEshopId(e.code)}
            className={`text-sm font-semibold rounded-md px-4 py-1.5 transition-colors cursor-pointer ${
              eshopId === e.code
                ? 'bg-surface text-primary shadow-sm'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            {e.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg border-b border-border text-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Sada</th>
              <th className="text-left font-semibold px-4 py-3 w-28">Sleva</th>
              <th className="text-left font-semibold px-4 py-3 w-28">Stav</th>
              <th className="text-right font-semibold px-4 py-3 w-44">Upraveno</th>
              <th className="text-right font-semibold px-4 py-3 w-36">Akce</th>
            </tr>
          </thead>
          <tbody>
            {sets === null && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted py-8">
                  Načítám…
                </td>
              </tr>
            )}
            {sets && sets.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted py-8">
                  Žádné sady zatím nejsou. Klikni na „Přidat sadu".
                </td>
              </tr>
            )}
            {sets?.map((s) => {
              const thumbs = s.item_thumbnails ?? [];
              const count = s.item_count ?? 0;
              const VISIBLE_THUMBS = 5;
              return (
                <tr key={s.id} className="border-b border-border last:border-0 hover:bg-bg/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {s.sada_image_url ? (
                        <img
                          src={s.sada_image_url}
                          alt=""
                          className="w-10 h-10 rounded-md object-contain bg-bg border border-border flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-bg border border-border flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => setEditing(s)}
                          className="text-text font-semibold hover:text-primary cursor-pointer text-left"
                        >
                          {s.nazev}
                        </button>
                        <div className="text-xs text-text-muted mt-0.5">
                          {s.sada_name ?? `Produkt #${s.sada_shopitem_id}`}
                          {s.sada_parent_code ? ` (${s.sada_parent_code})` : ''}
                        </div>
                      </div>
                      {thumbs.length > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {thumbs.slice(0, VISIBLE_THUMBS).map((url, i) => (
                            <img
                              key={i}
                              src={url}
                              alt=""
                              className="w-7 h-7 rounded object-contain bg-bg border border-border"
                              loading="lazy"
                            />
                          ))}
                          {count > VISIBLE_THUMBS && (
                            <div className="w-7 h-7 rounded bg-bg border border-border flex items-center justify-center text-[10px] font-semibold text-text-secondary">
                              +{count - VISIBLE_THUMBS}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-text font-semibold">
                    {s.rezim === 'darek' ? '🎁 Dárek' : `${s.sleva_procent} %`}
                  </td>
                  <td className="px-4 py-3">
                    {s.aktivni ? (
                      <span className="text-xs font-medium text-success">✓ Aktivní</span>
                    ) : (
                      <span className="text-xs font-medium text-text-muted">Vypnuto</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-text-muted text-xs">
                    {fmtDateTime(s.updated_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setEditing(s)}
                          className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-2.5 py-1 cursor-pointer transition-colors"
                        >
                          Upravit
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(s)}
                          className="text-xs font-medium text-danger hover:bg-danger-light border border-danger/30 rounded px-2.5 py-1 cursor-pointer transition-colors"
                        >
                          Smazat
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductSetModal
          set={editing === 'new' ? null : editing}
          eshopId={editing === 'new' ? eshopId : editing.eshop_id}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setToast({ msg, kind: 'success' });
            setEditing(null);
            load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}
